import type { JSX } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Budget, type Category } from "../api.js";
import { Money } from "../components/Money.js";
import { Modal } from "../components/Modal.js";
import { Field, Select, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.js";
import { majorToMinor, minorToMajorInput } from "../money.js";

export function Budgets(): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Budget | null>(null);
  const [showForm, setShowForm] = useState(false);
  const q = useQuery({ queryKey: ["budgets"], queryFn: () => api.list<Budget>("/budgets") });
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api.list<Category>("/categories") });
  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/budgets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["budgets"] }),
  });

  const catName = (id: string | null) => cats.data?.items.find((c) => c.id === id)?.name ?? "—";

  return (
    <>
      <PageHeader title="Budgets">
        <Button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          + Add budget
        </Button>
      </PageHeader>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data?.items.map((b) => (
              <TableRow key={b.id}>
                <TableCell>{b.period}</TableCell>
                <TableCell>{catName(b.category_id)}</TableCell>
                <TableCell className="text-right">
                  <Money amountMinor={b.amount_minor} native={b.currency} />
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditing(b);
                        setShowForm(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm("Delete budget?")) remove.mutate(b.id);
                      }}
                    >
                      Del
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {q.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground py-8 text-center">
                  No budgets.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
        }}
        title={editing ? "Edit budget" : "New budget"}
      >
        <BudgetForm
          initial={editing}
          categories={cats.data?.items ?? []}
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ["budgets"] });
          }}
        />
      </Modal>
    </>
  );
}

function BudgetForm({
  initial,
  categories,
  onDone,
}: {
  initial: Budget | null;
  categories: Category[];
  onDone: () => void;
}): JSX.Element {
  const [period, setPeriod] = useState(initial?.period ?? "monthly");
  const [categoryId, setCategoryId] = useState(initial?.category_id ?? "");
  const [amountMajor, setAmountMajor] = useState(
    initial ? minorToMajorInput(initial.amount_minor) : "0",
  );
  const [currency, setCurrency] = useState(initial?.currency ?? "EUR");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        period,
        category_id: categoryId || null,
        amount_minor: majorToMinor(amountMajor),
        currency: currency.toUpperCase(),
      };
      return initial ? api.update(`/budgets/${initial.id}`, body) : api.create("/budgets", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Period">
        <Select
          value={period}
          onChange={(e) => {
            setPeriod(e.target.value as Budget["period"]);
          }}
        >
          <option value="daily">daily</option>
          <option value="weekly">weekly</option>
          <option value="monthly">monthly</option>
          <option value="yearly">yearly</option>
        </Select>
      </Field>
      <Field label="Category">
        <Select
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
          }}
        >
          <option value="">— global (all categories) —</option>
          {categories
            .filter((c) => !c.is_archived)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.type} / {c.name}
              </option>
            ))}
        </Select>
      </Field>
      <Field label="Amount *">
        <TextInput
          required
          type="number"
          step="0.01"
          value={amountMajor}
          onChange={(e) => {
            setAmountMajor(e.target.value);
          }}
        />
      </Field>
      <Field label="Currency">
        <TextInput
          maxLength={3}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value.toUpperCase());
          }}
        />
      </Field>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving..." : "Save"}
      </Button>
    </form>
  );
}
